import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, FileUp, Loader2, Paperclip, X } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn, groupNairaInput, money, nairaInputToKobo } from '@/lib/utils';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalHeader,
  Select,
  Textarea,
} from '@/components/ui';

export interface NextInstallment {
  installmentNumber: number;
  dueDate: string;
  /** Kobo still owed on this installment. */
  amountDue: string;
}

const METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'CASH', label: 'Cash at the secretariat' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Telling us about a repayment.
 *
 * Two ways in, because they are genuinely different tasks. Paying the next
 * installment is the common case and needs no thought — the amount is already
 * known, so the member is only asked for the date and a receipt. Paying some
 * other amount is deliberate, and only then is a figure requested.
 *
 * This raises a claim; it does not move the balance. An officer checks the
 * receipt against the account first, and the copy says so.
 */
export function RepayModal({
  loanId,
  loanNumber,
  nextInstallment,
  onClose,
}: {
  loanId: string;
  loanNumber: string;
  nextInstallment?: NextInstallment | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<'installment' | 'custom'>(
    nextInstallment ? 'installment' : 'custom',
  );
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentTime, setPaymentTime] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState<{ key: string; fileName: string; mimeType: string } | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the server finds a match; cleared as soon as the figures move. */
  const [duplicate, setDuplicate] = useState<string | null>(null);

  const payingInstallment = mode === 'installment' && nextInstallment;
  const amountKobo = payingInstallment
    ? BigInt(nextInstallment.amountDue)
    : nairaInputToKobo(amount || '0');

  /** Straight to storage; the file never passes through the API. */
  async function upload(file: File) {
    setError(null);
    setUploading(true);
    try {
      const signed = await api.post<{ uploadUrl: string; key: string }>(
        '/repayments/declarations/receipt-url',
        { fileName: file.name, mimeType: file.type, fileSize: file.size },
      );

      const response = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status}).`);

      setReceipt({ key: signed.key, fileName: file.name, mimeType: file.type });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not upload that file. Try again, or a different photo.',
      );
    } finally {
      setUploading(false);
    }
  }

  const submit = useMutation({
    mutationFn: (allowDuplicate: boolean) =>
      api.post<{ message: string }>('/repayments/declare', {
        loanId,
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
        // Naira, as every other money field on the API is.
        amount: payingInstallment
          ? (Number(nextInstallment.amountDue) / 100).toFixed(2)
          : amount.replace(/,/g, ''),
        paymentDate,
        paymentMethod,
        ...(paymentTime ? { paymentTime } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(receipt
          ? {
              receiptKey: receipt.key,
              receiptFileName: receipt.fileName,
              receiptMimeType: receipt.mimeType,
            }
          : {}),
      }),
    onSuccess: (result) => {
      toast.success(result.message);
      void queryClient.invalidateQueries({ queryKey: ['my-declarations'] });
      onClose();
    },
    onError: (caught) => {
      // 409 is not a refusal, it is a question: we have one like this already.
      if (caught instanceof ApiError && caught.status === 409) {
        setDuplicate(caught.message);
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not send that.');
    },
  });

  const valid = amountKobo > 0n && Boolean(paymentDate) && Boolean(paymentMethod) && Boolean(receipt);

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title="Repay" description={loanNumber} onClose={onClose} />

      <div className="space-y-5 p-6">
        {error && <Alert tone="danger">{error}</Alert>}


        {/* Choose what is being paid. */}
        {nextInstallment && (
          <div className="grid gap-2 sm:grid-cols-2">
            <ModeCard
              active={mode === 'installment'}
              onClick={() => setMode('installment')}
              title="Next installment"
              detail={`${money(nextInstallment.amountDue)} · due ${formatLongDate(
                new Date(nextInstallment.dueDate),
              )}`}
            />
            <ModeCard
              active={mode === 'custom'}
              onClick={() => setMode('custom')}
              title="A different amount"
              detail="Part payment, or more than one installment"
            />
          </div>
        )}

        {mode === 'custom' && (
          <Field label="Amount paid" htmlFor="repayAmount" hint="In naira." required>
            <Input
              id="repayAmount"
              className="numeric"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                setAmount(groupNairaInput(e.target.value));
                setDuplicate(null);
              }}
              placeholder="25,000"
              autoFocus
            />
          </Field>
        )}

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Date paid" htmlFor="repayDate" required>
            <Input
              id="repayDate"
              type="date"
              max={today}
              value={paymentDate}
              onChange={(e) => {
                setPaymentDate(e.target.value);
                setDuplicate(null);
              }}
            />
          </Field>

          <Field label="Time paid" htmlFor="repayTime" hint="If you know it.">
            <Input
              id="repayTime"
              type="time"
              value={paymentTime}
              onChange={(e) => setPaymentTime(e.target.value)}
            />
          </Field>

          <Field label="How you paid" htmlFor="repayMethod" required>
            <Select
              id="repayMethod"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="" disabled>
                Choose one
              </option>
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Receipt */}
        <Field
          label="Receipt"
          htmlFor="repayReceipt"
          hint="A screenshot or photo of your transfer. This is how we find your payment."
          required
        >
          <input
            ref={fileInput}
            id="repayReceipt"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />

          {receipt ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-success/30 bg-success-soft px-3.5 py-2.5">
              <span className="flex min-w-0 items-center gap-2 text-sm text-success-soft-foreground">
                <Check size={15} className="shrink-0" />
                <span className="truncate">{receipt.fileName}</span>
              </span>
              <button
                type="button"
                onClick={() => setReceipt(null)}
                aria-label="Remove receipt"
                className="shrink-0 rounded-lg p-1 text-foreground-secondary hover:bg-background"
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              full
              loading={uploading}
              onClick={() => fileInput.current?.click()}
            >
              {uploading ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <FileUp size={15} /> Attach a receipt
                </>
              )}
            </Button>
          )}
        </Field>

        <Field label="Anything else? (optional)" htmlFor="repayNote">
          <Textarea
            id="repayNote"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Paid from my GTBank account"
          />
        </Field>

        <Alert tone="info">
          Your balance changes once an officer confirms this against the account, not before.
        </Alert>

        {duplicate && (
          <Alert tone="warning" title="This may already be recorded">
            {duplicate} If that was a separate payment, send this one as well. If not, close this
            and check your payment history.
          </Alert>
        )}
      </div>

      <div className="space-y-3 border-t border-border p-4 sm:flex sm:items-center sm:justify-between sm:gap-4 sm:space-y-0">
        <p className="flex items-baseline gap-2 text-sm">
          <span className="text-foreground-secondary">Amount</span>
          <span className="numeric font-semibold text-foreground">
            {amountKobo > 0n ? money(amountKobo.toString()) : '—'}
          </span>
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" className="flex-1 sm:flex-none" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1 sm:flex-none"
            loading={submit.isPending}
            disabled={!valid}
            onClick={() => submit.mutate(Boolean(duplicate))}
          >
            <Paperclip size={15} /> {duplicate ? 'Send anyway' : 'Send'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ModeCard({
  active,
  onClick,
  title,
  detail,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-xl border p-3.5 text-left transition-colors',
        active
          ? 'border-primary bg-primary-soft'
          : 'border-border bg-background hover:bg-accent',
      )}
    >
      <span className="block text-sm font-bold text-foreground">{title}</span>
      <span className="mt-0.5 block text-xs text-foreground-secondary">{detail}</span>
    </button>
  );
}
